import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { formatCurrency } from '../utils/currency';
import { CreditCardForm } from '../components/CreditCardForm';

export const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [processingPaymentId, setProcessingPaymentId] = useState<string | null>(null);
  const [sellerCode, setSellerCode] = useState('');

  const subscriptionPrice = 49.90;
  const isSubscribed = user?.subscriptionStatus === 'active' && user?.subscriptionExpiry && new Date(user.subscriptionExpiry) > new Date();

  const completeActivation = () => {
    setMessage({ type: 'success', text: 'Assinatura ativada com sucesso!' });
    refreshUser();
    setTimeout(() => setShowCardForm(false), 2000);
  };

  // While the first subscription charge is under issuer review, poll the user
  // profile until the subscription activates. The webhook flips
  // subscriptionStatus to 'active' once Mercado Pago approves the charge; a
  // preapproval id is not a PIX payment id, so we watch activation on /auth/me
  // rather than the payment-status endpoint. Give up after a few minutes so the
  // spinner never spins forever.
  useEffect(() => {
    if (!processingPaymentId) return;

    let cancelled = false;
    let elapsed = 0;
    const interval = setInterval(async () => {
      elapsed += 5000;
      try {
        const res = await api.get('/auth/me');
        const status = res.data?.user?.subscriptionStatus;
        if (cancelled) return;
        if (status === 'active') {
          setProcessingPaymentId(null);
          setLoading(false);
          completeActivation();
          return;
        }
      } catch (error) {
        // Transient — keep polling.
        console.debug('Subscription activation poll failed, will retry:', error);
      }
      if (!cancelled && elapsed >= 180000) {
        setProcessingPaymentId(null);
        setLoading(false);
        setMessage({
          type: 'error',
          text: 'O pagamento ainda está em análise. Você será notificado assim que a assinatura for ativada.'
        });
      }
    }, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [processingPaymentId]);

  const handleCardSuccess = async (token: string, installments: number, cardInfo?: { lastFour: string; brand: string }) => {
    try {
      setLoading(true);
      setMessage(null);
      const response = await api.post('/payments/subscription-card', {
        cardToken: token,
        installments,
        cardLastFour: cardInfo?.lastFour,
        cardBrand: cardInfo?.brand,
        // Optional — omitted entirely when the customer had no seller with them.
        ...(sellerCode ? { sellerCode } : {})
      });

      // A card charge can land as 'processing' (issuer review) rather than
      // approved. Don't claim success — show a processing state and poll until
      // the payment resolves (the webhook activates the subscription once MP
      // approves it). This avoids telling the user "activated" when it isn't yet.
      if (response.status === 202 || response.data?.data?.status === 'processing') {
        setProcessingPaymentId(response.data?.data?.subscription?.id || null);
        setMessage({ type: 'success', text: 'Pagamento em processamento… aguarde a confirmação.' });
        return;
      }

      completeActivation();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Falha ao processar pagamento' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-500 to-orange-400 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 bg-white/20 rounded-full -top-48 -left-48 animate-pulse"></div>
        <div className="absolute w-64 h-64 bg-white/10 rounded-full top-1/4 -right-32 animate-bounce" style={{ animationDuration: '3s' }}></div>
      </div>

      {/* Header */}
      <header className="bg-gradient-to-r from-orange-600 to-orange-500 shadow-lg sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-orange-700 rounded-lg transition-colors" aria-label="Voltar">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className="text-white text-xl font-bold">Plano Mensal</h1>
            <div className="w-10"></div>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-4 relative z-10">
        {/* Messages */}
        {message && (
          <div className={`mb-4 p-3 border-l-4 rounded-r-lg ${
            message.type === 'success' ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
          }`}>
            <p className={`text-sm font-medium ${message.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
              {message.text}
            </p>
          </div>
        )}

        {/* Active subscription banner */}
        {isSubscribed && (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-center mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <h3 className="text-center text-base font-bold text-green-900 mb-1">Assinatura Ativa</h3>
            <p className="text-center text-green-700 text-sm">
              Válida até: {user?.subscriptionExpiry ? new Date(user.subscriptionExpiry).toLocaleDateString('pt-BR') : '-'}
            </p>
            {/* Minutes usage */}
            <div className="mt-3 bg-white rounded-lg p-3">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Minutos usados</span>
                <span className="text-sm font-bold text-gray-900">{user?.subscriptionMinutesUsed || 0} / 75 min</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-green-500 h-2.5 rounded-full transition-all"
                  style={{ width: `${Math.min(100, ((user?.subscriptionMinutesUsed || 0) / 75) * 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-green-600 font-semibold mt-1 text-center">
                {Math.max(0, 75 - (user?.subscriptionMinutesUsed || 0))} minutos restantes
              </p>
            </div>
            <p className="text-center text-green-600 text-xs mt-2">
              Gerencie sua assinatura em Configurações da Conta
            </p>
          </div>
        )}

        {/* Plan Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden mb-4">
          <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-4 py-3 text-center">
            <h2 className="text-white text-xl font-bold">Plano Mensal</h2>
            <p className="text-white/90 text-xs">75 minutos por mês para usar como quiser!</p>
          </div>

          <div className="p-4">
            {/* Price */}
            <div className="text-center mb-4">
              <div className="inline-block bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl px-4 py-3 mb-3">
                <div className="flex items-center justify-center space-x-2 mb-1">
                  <span className="text-gray-500 text-sm line-through">R$ 75,00</span>
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">-33%</span>
                </div>
                <div className="text-orange-600 text-4xl font-bold mb-0.5">{formatCurrency(subscriptionPrice)}</div>
                <p className="text-gray-600 text-xs font-medium">por mês</p>
                <p className="text-orange-600 text-xs font-semibold mt-1">⏱️ 75 minutos inclusos</p>
              </div>
            </div>

            {/* Value comparison */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-gray-700 text-sm font-medium">75 min avulso:</span>
                <span className="text-gray-500 text-sm line-through">R$ 75,00</span>
              </div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-orange-700 text-sm font-bold">Com Plano Mensal:</span>
                <span className="text-orange-600 font-bold text-lg">{formatCurrency(subscriptionPrice)}</span>
              </div>
              <div className="border-t-2 border-green-300 pt-1.5 mt-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-green-700 text-sm font-bold">Você economiza:</span>
                  <span className="text-green-600 font-bold text-xl">R$ 25,10</span>
                </div>
              </div>
            </div>

            {/* Subscribe button */}
            {!isSubscribed && (
              <>
                {/* Optional seller code */}
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Código do vendedor <span className="font-normal text-gray-500">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    value={sellerCode}
                    onChange={(e) => setSellerCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="00000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-center tracking-widest font-mono focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Se um vendedor da UpCar te atendeu, informe o código de 5 dígitos dele.
                  </p>
                </div>

                {/* Terms agreement checkbox */}
                <label className="flex items-start space-x-2 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-xs text-gray-600">
                    Li e concordo com os{' '}
                    <Link
                      to="/terms-and-conditions#assinatura"
                      className="text-orange-600 hover:text-orange-700 underline font-medium"
                    >
                      Termos e Condições da Assinatura
                    </Link>
                    , incluindo a cobrança recorrente mensal de R$ 49,90 no cartão de crédito.
                  </span>
                </label>

                <button
                  onClick={() => { setShowCardForm(true); setMessage(null); }}
                  disabled={loading || !termsAccepted}
                  className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white font-bold py-4 px-6 rounded-xl transition-all shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed text-base mb-4"
                >
                  Assinar Plano
                </button>
              </>
            )}

            {/* Benefits */}
            <div className="space-y-2">
              {[
                { title: '75 minutos por mês', desc: 'Use em qualquer aspirador da rede' },
                { title: 'Acesso a todas as máquinas', desc: 'Escolha qualquer aspirador disponível' },
                { title: 'Economia garantida', desc: 'Pague menos que o preço avulso' },
                { title: 'Cancele quando quiser', desc: 'Sem multas ou taxas de cancelamento' },
              ].map((b, i) => (
                <div key={i} className="flex items-start space-x-2 bg-orange-50 rounded-lg p-2.5">
                  <div className="w-5 h-5 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-sm">{b.title}</p>
                    <p className="text-gray-600 text-xs">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-blue-800 text-xs">
            <strong>Como funciona:</strong> Pagamento com cartão de crédito. Após a aprovação, você terá 75 minutos por mês para usar em qualquer aspirador.
            A assinatura é renovada automaticamente todo mês no seu cartão.
          </p>
        </div>
      </main>

      {/* Credit Card Modal */}
      {showCardForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-3">
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Pagamento com Cartão</h3>
              <p className="text-sm text-gray-600">Assinatura mensal - Renovação automática</p>
            </div>

            {message && (
              <div className={`mb-4 p-3 rounded-lg ${
                message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                <span className="text-sm font-medium">{message.text}</span>
              </div>
            )}

            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-3 mb-4 text-center">
              <p className="text-xs text-orange-700 mb-1">Valor Mensal</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(subscriptionPrice)}</p>
              <p className="text-xs text-orange-600 mt-1">75 minutos/mês • Renovação automática</p>
            </div>

            {processingPaymentId ? (
              <div className="text-center py-6">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mb-3"></div>
                <p className="text-sm text-gray-600">
                  Confirmando seu pagamento com o banco emissor. Isso pode levar alguns instantes — você não precisa fazer nada.
                </p>
              </div>
            ) : (
              <CreditCardForm
                amount={subscriptionPrice}
                onSuccess={handleCardSuccess}
                onError={(msg) => setMessage({ type: 'error', text: msg })}
                onCancel={() => { setShowCardForm(false); setMessage(null); }}
                loading={loading}
              />
            )}
          </div>
        </div>
      )}

    </div>
  );
};
